import type {
  NavGroupItem,
  NavItem,
  NavItemInternal,
} from "types/client/navigation";

import React from "react";
import config from "configs/app";
import { toaster } from "toolkit/chakra/toaster";
import useAccount from "lib/web3/useAccount";
import { useRouter } from "next/router";

interface ReturnType {
  mainNavItems: Array<NavItem | NavGroupItem>;
  accountNavItems: Array<NavItem>;
}

export function isGroupItem(
  item: NavItem | NavGroupItem,
): item is NavGroupItem {
  return "subItems" in item;
}

export function isInternalItem(item: NavItem): item is NavItemInternal {
  return "nextRoute" in item;
}

export default function useNavItems(): ReturnType {
  const router = useRouter();
  const pathname = router.pathname;
  const query = router.query;
  const tab = query.tab;
  const { address, isConnected, isConnecting } = useAccount();
  const handleAssetManagementClick = React.useCallback<
    React.MouseEventHandler<HTMLAnchorElement>
  >(
    (event) => {
      if (!address || !isConnected || isConnecting) {
        event.preventDefault();
        toaster.warning({
          title: "Warning",
          description: "You should first connect your wallet.",
        });
      }
    },
    [address, isConnected, isConnecting],
  );

  return React.useMemo(() => {
    const topAccounts: NavItem | null = !config.UI.views.address.hiddenViews
      ?.top_accounts
      ? {
          text: "Top Doto accounts",
          nextRoute: { pathname: "/accounts" as const },
          icon: "top-accounts",
          isActive: pathname === "/accounts",
        }
      : null;
    const blocks: NavItem | null = {
      text: "Blocks",
      nextRoute: { pathname: "/blocks" as const },
      icon: "block",
      isActive:
        pathname === "/blocks" ||
        pathname === "/block/[height_or_hash]" ||
        pathname === "/chain/[chain-slug]/block/[height_or_hash]",
    };
    const txs: NavItem | null = {
      text: "Transactions",
      nextRoute: { pathname: "/txs" as const },
      icon: "transactions",
      isActive:
        // sorry, but this is how it was designed
        (pathname === "/txs" &&
          (!config.features.zetachain.isEnabled ||
            !tab ||
            !tab.includes("cctx"))) ||
        pathname === "/tx/[hash]" ||
        pathname === "/chain/[chain-slug]/tx/[hash]",
    };
    const cctxs: NavItem | null = config.features.zetachain.isEnabled
      ? {
          text: "Cross-chain transactions",
          nextRoute: { pathname: "/txs" as const, query: { tab: "cctx" } },
          icon: "interop",
          isActive:
            pathname === "/cc/tx/[hash]" ||
            (pathname === "/txs" && tab?.includes("cctx")),
        }
      : null;
    const operations: NavItem | null = config.features.tac.isEnabled
      ? {
          text: "Operations",
          nextRoute: { pathname: "/operations" as const },
          icon: "operation",
          isActive:
            pathname === "/operations" || pathname === "/operation/[id]",
        }
      : null;
    const internalTxs: NavItem | null = {
      text: "Internal transactions",
      nextRoute: { pathname: "/internal-txs" as const },
      icon: "internal_txns",
      isActive: pathname === "/internal-txs",
    };
    const userOps: NavItem | null = config.features.userOps.isEnabled
      ? {
          text: "User operations",
          nextRoute: { pathname: "/ops" as const },
          icon: "user_op",
          isActive:
            pathname === "/ops" ||
            pathname === "/op/[hash]" ||
            pathname === "/chain/[chain-slug]/op/[hash]",
        }
      : null;

    const verifiedContracts: NavItem | null = {
      text: "Verified contracts",
      nextRoute: { pathname: "/verified-contracts" as const },
      icon: "verified",
      isActive: pathname === "/verified-contracts",
    };
    const nameLookup = config.features.nameServices.isEnabled
      ? {
          text: "Name services lookup",
          nextRoute: { pathname: "/name-services" as const },
          icon: "name_services",
          isActive: pathname.startsWith("/name-services"),
        }
      : null;
    const validators = config.features.validators.isEnabled
      ? {
          text: "Validators",
          nextRoute: { pathname: "/validators" as const },
          icon: "validator",
          isActive:
            pathname === "/validators" || pathname === "/validators/[id]",
        }
      : null;
    const validatorSet: NavItem = {
      text: "Validators",
      nextRoute: { pathname: "/validator-set" as const },
      icon: "validator",
      isActive: pathname === "/validator-set",
    };
    const mudWorlds = config.features.mudFramework.isEnabled
      ? {
          text: "MUD worlds",
          nextRoute: { pathname: "/mud-worlds" as const },
          icon: "MUD_menu",
          isActive: pathname === "/mud-worlds",
        }
      : null;
    const epochs = config.features.celo.isEnabled
      ? {
          text: "Epochs",
          nextRoute: { pathname: "/epochs" as const },
          icon: "hourglass",
          isActive: pathname.startsWith("/epochs"),
        }
      : null;

    const blockchainNavItems: Array<NavItem> | Array<Array<NavItem>> = [
      txs,
      operations,
      internalTxs,
      cctxs,
      userOps,
      blocks,
      epochs,
      topAccounts,
      mudWorlds,
      validators,
      validatorSet,
      verifiedContracts,
      nameLookup,
      config.features.beaconChain.isEnabled && {
        text: "Deposits",
        nextRoute: { pathname: "/deposits" as const },
        icon: "arrows/south-east",
        isActive: pathname === "/deposits",
      },
      config.features.beaconChain.isEnabled && {
        text: "Withdrawals",
        nextRoute: { pathname: "/withdrawals" as const },
        icon: "arrows/north-east",
        isActive: pathname === "/withdrawals",
      },
    ].filter(Boolean);

    // const blockScoutNavItem = [
    //   {
    //     text: "Swap",
    //     nextRoute: { pathname: "/apps/uniswap?action=connect" as const },
    //     icon: "token",
    //     isActive: pathname === "/apps/uniswap?action=connect",
    //   },
    //   {
    //     text: "Faucet",
    //     nextRoute: {
    //       pathname: "/apps/peanut-protocol?action=connect" as const,
    //     },
    //     icon: "token-transfers",
    //     isActive: pathname === "/apps/peanut-protocol?action=connect",
    //   },
    //   config.features.pools.isEnabled && {
    //     text: "DEX tracker",
    //     nextRoute: { pathname: "/pools" as const },
    //     icon: "dex-tracker",
    //     isActive: pathname === "/pools" || pathname.startsWith("/pool/"),
    //   },
    // ].filter(Boolean);

    const tokensNavItems = [
      {
        text: "Tokens",
        nextRoute: { pathname: "/tokens" as const },
        icon: "token",
        isActive: pathname === "/tokens" || pathname.startsWith("/token/"),
      },
      {
        text: "Token transfers",
        nextRoute: { pathname: "/token-transfers" as const },
        icon: "token-transfers",
        isActive: pathname === "/token-transfers",
      },
      config.features.pools.isEnabled && {
        text: "DEX tracker",
        nextRoute: { pathname: "/pools" as const },
        icon: "dex-tracker",
        isActive: pathname === "/pools" || pathname.startsWith("/pool/"),
      },
    ].filter(Boolean);

    const otherNavItems: Array<NavItem> | Array<Array<NavItem>> = [
      config.features.blockchainInteraction.isEnabled && {
        text: "Asset Management",
        nextRoute: { pathname: "/asset" as const },
        // icon: 'wallet' as const,
        isActive: pathname === "/asset" || pathname.startsWith("/asset/"),
        onClick: handleAssetManagementClick,
      },
      // config.features.opSuperchain.isEnabled
      //   ? {
      //       text: "Verify contract",
      //       // TODO @tom2drum adjust URL to Vera
      //       url: "https://vera.blockscout.com",
      //     }
      //   :
      {
        text: "Verify contract",
        nextRoute: { pathname: "/contract-verification" as const },
        isActive: pathname.startsWith("/contract-verification"),
      },
      config.features.gasTracker.isEnabled && {
        text: "Gas tracker",
        nextRoute: { pathname: "/gas-tracker" as const },
        isActive: pathname.startsWith("/gas-tracker"),
      },
      {
        text: "Charts & stats",
        nextRoute: { pathname: "/stats" as const },
        // icon: "stats",
        isActive: pathname.startsWith("/stats"),
      },
      // {
      //   text: "API",
      //   nextRoute: { pathname: "/api-docs" as const },
      //   // icon: "restAPI",
      //   isActive: pathname.startsWith("/api-docs"),
      // },
      config.features.publicTagsSubmission.isEnabled && {
        text: "Submit public tag",
        nextRoute: { pathname: "/public-tags/submit" as const },
        isActive: pathname.startsWith("/public-tags/submit"),
      },
      ...config.UI.navigation.otherLinks,
    ].filter(Boolean);

    const mainNavItems: ReturnType["mainNavItems"] = [
      // {
      //   text: "Blockscout DeFi",
      //   icon: "token",
      //   isActive: blockScoutNavItem
      //     .flat()
      //     .some((item) => isInternalItem(item) && item.isActive),
      //   subItems: blockScoutNavItem,
      // },
      {
        text: "Blockchain",
        icon: "globe-b",
        isActive: blockchainNavItems
          .flat()
          .some((item) => isInternalItem(item) && item.isActive),
        subItems: blockchainNavItems,
      },
      {
        text: "Multi Signature",
        url: "https://multisig.dotone.online",
      },
      {
        text: "Dot Marketcap",
        url: "https://market.dotone.online",
      },
      {
        text: "Tokens",
        icon: "token",
        isActive: tokensNavItems
          .flat()
          .some((item) => isInternalItem(item) && item.isActive),
        subItems: tokensNavItems,
      },
      config.features.marketplace.isEnabled
        ? {
            text: "DApps",
            nextRoute: { pathname: "/apps" as const },
            icon: "apps",
            isActive:
              pathname.startsWith("/app") ||
              pathname.startsWith("/essential-dapps"),
          }
        : null,
      // statsNavItem,
      // apiNavItem,
      {
        text: "Other",
        icon: "gear",
        isActive: otherNavItems
          .flat()
          .some((item) => isInternalItem(item) && item.isActive),
        subItems: otherNavItems,
      },
    ].filter(Boolean);

    const accountNavItems: ReturnType["accountNavItems"] = [
      {
        text: "Watch list",
        nextRoute: { pathname: "/account/watchlist" as const },
        icon: "watchlist",
        isActive: pathname === "/account/watchlist",
      },
      {
        text: "Private tags",
        nextRoute: { pathname: "/account/tag-address" as const },
        icon: "privattags",
        isActive: pathname === "/account/tag-address",
      },
      {
        text: "API keys",
        nextRoute: { pathname: "/account/api-key" as const },
        icon: "API",
        isActive: pathname === "/account/api-key",
      },
      {
        text: "Custom ABI",
        nextRoute: { pathname: "/account/custom-abi" as const },
        icon: "ABI",
        isActive: pathname === "/account/custom-abi",
      },
      config.features.addressVerification.isEnabled && {
        text: "Verified addrs",
        nextRoute: { pathname: "/account/verified-addresses" as const },
        icon: "verified",
        isActive: pathname === "/account/verified-addresses",
      },
    ].filter(Boolean);

    return { mainNavItems, accountNavItems };
  }, [pathname, tab, handleAssetManagementClick]);
}
